/**
 * i18n.ts - Internationalization System (TypeScript)
 *
 * Handles translation loading, language switching, and DOM updates
 * for multi-language support.
 */

export type LanguageCode =
  'ar' | 'bg' | 'bn' | 'cs' | 'da' | 'de' | 'el' | 'en' | 'es' | 'fi' |
  'fr' | 'hi' | 'hu' | 'id' | 'it' | 'ja' | 'ko' | 'no' | 'pl' | 'pt' |
  'ro' | 'ru' | 'sr' | 'sv' | 'tr' | 'uk' | 'zh';

export interface PluralForms {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}

export interface Translations {
  [key: string]: string | Translations | PluralForms;
}

export interface TranslationsMap {
  [lang: string]: Translations;
}

export interface I18nEventDetail {
  language: LanguageCode;
}

export interface ReplacementsMap {
  [key: string]: string | number;
}

export class I18n {
  private currentLanguage: LanguageCode;
  private translations: TranslationsMap = {};
  private readonly fallbackLanguage: LanguageCode = 'en';
  private readonly supportedLanguages: ReadonlyArray<LanguageCode> = [
    'ar', 'bg', 'bn', 'cs', 'da', 'de', 'el', 'en', 'es', 'fi',
    'fr', 'hi', 'hu', 'id', 'it', 'ja', 'ko', 'no', 'pl', 'pt',
    'ro', 'ru', 'sr', 'sv', 'tr', 'uk', 'zh'
  ];

  constructor() {
    this.currentLanguage = this.detectLanguage();
  }

  /**
   * Get plural category for a given count and language
   * Based on CLDR pluralization rules
   */
  private getPluralCategory(count: number, lang: LanguageCode): keyof PluralForms {
    const absCount = Math.abs(count);
    const n = absCount;
    const mod10 = n % 10;
    const mod100 = n % 100;

    // Languages with no plural forms (Chinese, Japanese, Korean, etc.)
    if (['zh', 'ja', 'ko', 'id', 'tr', 'th', 'vi'].includes(lang)) {
      return 'other';
    }

    // Slavic languages (Russian, Ukrainian, Serbian, etc.)
    if (['ru', 'uk', 'sr', 'hr', 'bs'].includes(lang)) {
      if (mod10 === 1 && mod100 !== 11) return 'one';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
      return 'many';
    }

    // Polish
    if (lang === 'pl') {
      if (n === 1) return 'one';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
      return 'many';
    }

    // Czech, Slovak
    if (['cs', 'sk'].includes(lang)) {
      if (n === 1) return 'one';
      if (n >= 2 && n <= 4) return 'few';
      return 'other';
    }

    // Arabic
    if (lang === 'ar') {
      if (n === 0) return 'zero';
      if (n === 1) return 'one';
      if (n === 2) return 'two';
      if (mod100 >= 3 && mod100 <= 10) return 'few';
      if (mod100 >= 11 && mod100 <= 99) return 'many';
      return 'other';
    }

    // Romanian
    if (lang === 'ro') {
      if (n === 1) return 'one';
      if (n === 0 || (mod100 >= 1 && mod100 <= 19)) return 'few';
      return 'other';
    }

    // Default: Most Western European languages (English, German, Spanish, etc.)
    if (n === 1) return 'one';
    return 'other';
  }

  /**
   * Check if a value is a PluralForms object
   */
  private isPluralForms(value: any): value is PluralForms {
    return typeof value === 'object' && value !== null && 'other' in value;
  }

  /**
   * Detect user's preferred language from localStorage or browser settings
   */
  private detectLanguage(): LanguageCode {
    // Check localStorage first
    const savedLang = localStorage.getItem('language');
    if (savedLang && this.isSupportedLanguage(savedLang)) {
      return savedLang as LanguageCode;
    }

    // Check browser language
    const browserLang = navigator.language || (navigator as any).userLanguage;
    const langCode = browserLang.split('-')[0].toLowerCase();

    // Map to supported languages
    return this.isSupportedLanguage(langCode) ? (langCode as LanguageCode) : 'en';
  }

  /**
   * Type guard to check if a string is a supported language code
   */
  private isSupportedLanguage(lang: string): lang is LanguageCode {
    return this.supportedLanguages.includes(lang as LanguageCode);
  }

  /**
   * Load translation file for a specific language
   */
  async loadTranslations(lang: LanguageCode): Promise<boolean> {
    try {
      const response = await fetch(`/i18n/${lang}.json`);
      if (!response.ok) {
        throw new Error(`Failed to load ${lang} translations`);
      }
      this.translations[lang] = await response.json();
      return true;
    } catch (error) {
      console.error(`Error loading translations for ${lang}:`, error);
      return false;
    }
  }

  /**
   * Initialize i18n system - load translations and apply to DOM
   */
  async init(): Promise<void> {
    // Load current language translations
    await this.loadTranslations(this.currentLanguage);

    // Load fallback language if different
    if (this.currentLanguage !== this.fallbackLanguage) {
      await this.loadTranslations(this.fallbackLanguage);
    }

    // Apply translations to the page
    this.applyTranslations();

    // Add class to body to indicate i18n is ready
    document.body.classList.add('i18n-ready');

    // Update language selector if present
    this.updateLanguageSelector();

    // Dispatch event to notify that i18n is ready
    this.dispatchEvent('i18nReady', { language: this.currentLanguage });
  }

  /**
   * Get translation by key with optional placeholder replacements
   */
  t(key: string, replacements: ReplacementsMap = {}): string {
    // Try to get from current language
    let translationValue = this.getNestedValue(this.translations[this.currentLanguage], key);

    // Fallback to English if not found
    if (!translationValue && this.currentLanguage !== this.fallbackLanguage) {
      translationValue = this.getNestedValue(this.translations[this.fallbackLanguage], key);
    }

    // Return key if translation not found
    if (!translationValue) {
      console.warn(`Translation not found for key: ${key}`);
      return key;
    }

    // Handle pluralization
    let translation: string;
    if (this.isPluralForms(translationValue) && 'count' in replacements) {
      const count = Number(replacements.count);
      const pluralCategory = this.getPluralCategory(count, this.currentLanguage);

      // Try to get the specific plural form, fallback to 'other'
      translation = translationValue[pluralCategory] || translationValue.other;
    } else if (typeof translationValue === 'string') {
      translation = translationValue;
    } else {
      // If it's a PluralForms but no count provided, use 'other' as default
      if (this.isPluralForms(translationValue)) {
        translation = translationValue.other;
      } else {
        console.warn(`Invalid translation value for key: ${key}`);
        return key;
      }
    }

    // Replace placeholders
    let result = translation;
    Object.entries(replacements).forEach(([placeholder, value]) => {
      result = result.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), String(value));
    });

    return result;
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: Translations | undefined, path: string): string | PluralForms | null {
    if (!obj) return null;

    const value = path.split('.').reduce<any>((current, key) => {
      return current?.[key];
    }, obj);

    // Return the value if it's a string or PluralForms object
    if (typeof value === 'string' || this.isPluralForms(value)) {
      return value;
    }

    return null;
  }

  /**
   * Apply translations to all elements with data-i18n attributes
   */
  applyTranslations(): void {
    // Translate text content
    document.querySelectorAll('[data-i18n]').forEach(element => {
      const key = element.getAttribute('data-i18n');
      if (key) {
        element.textContent = this.t(key);
      }
    });

    // Translate placeholders
    document.querySelectorAll('[data-i18n-placeholder]').forEach(element => {
      const key = element.getAttribute('data-i18n-placeholder');
      if (key && (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
        (element as HTMLInputElement | HTMLTextAreaElement).placeholder = this.t(key);
      }
    });

    // Translate titles (tooltips)
    document.querySelectorAll('[data-i18n-title]').forEach(element => {
      const key = element.getAttribute('data-i18n-title');
      if (key && element instanceof HTMLElement) {
        element.title = this.t(key);
      }
    });

    // Translate values (for buttons, etc.)
    document.querySelectorAll('[data-i18n-value]').forEach(element => {
      const key = element.getAttribute('data-i18n-value');
      if (key && (element instanceof HTMLInputElement || element instanceof HTMLButtonElement)) {
        (element as HTMLInputElement | HTMLButtonElement).value = this.t(key);
      }
    });

    // Update HTML lang attribute
    document.documentElement.lang = this.currentLanguage;
  }

  /**
   * Change the current language
   */
  async changeLanguage(lang: LanguageCode): Promise<void> {
    if (this.currentLanguage === lang) return;

    // Load translations if not already loaded
    if (!this.translations[lang]) {
      const success = await this.loadTranslations(lang);
      if (!success) return;
    }

    // Update current language
    this.currentLanguage = lang;
    localStorage.setItem('language', lang);

    // Apply translations
    this.applyTranslations();

    // Add class to body to indicate i18n is ready
    document.body.classList.add('i18n-ready');

    // Update language selector
    this.updateLanguageSelector();

    // Dispatch event for other components to react
    this.dispatchEvent('languageChanged', { language: lang });
  }

  /**
   * Update language selector UI element
   */
  private updateLanguageSelector(): void {
    const selector = document.getElementById('language-selector') as HTMLSelectElement | null;
    if (selector) {
      selector.value = this.currentLanguage;
    }

    // Also update landing page language selector
    const landingSelector = document.getElementById('landing-language-selector') as HTMLSelectElement | null;
    if (landingSelector) {
      landingSelector.value = this.currentLanguage;
    }
  }

  /**
   * Get current language code
   */
  getCurrentLanguage(): LanguageCode {
    return this.currentLanguage;
  }

  /**
   * Get locale code for date/time formatting
   * Uses browser's full locale if available, falls back to language-based locale
   */
  getLocale(): string {
    // Try to use the browser's full locale (e.g., de-CH, en-GB)
    const browserLocale = navigator.language || (navigator as any).userLanguage;

    // If the browser locale starts with our current language, use it
    if (browserLocale.toLowerCase().startsWith(this.currentLanguage)) {
      return browserLocale;
    }

    // Otherwise, map to default locale for the language
    const localeMap: Record<LanguageCode, string> = {
      'ar': 'ar-SA',
      'bg': 'bg-BG',
      'bn': 'bn-BD',
      'cs': 'cs-CZ',
      'da': 'da-DK',
      'de': 'de-DE',
      'el': 'el-GR',
      'en': 'en-US',
      'es': 'es-ES',
      'fi': 'fi-FI',
      'fr': 'fr-FR',
      'hi': 'hi-IN',
      'hu': 'hu-HU',
      'id': 'id-ID',
      'it': 'it-IT',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
      'no': 'no-NO',
      'pl': 'pl-PL',
      'pt': 'pt-PT',
      'ro': 'ro-RO',
      'ru': 'ru-RU',
      'sr': 'sr-RS',
      'sv': 'sv-SE',
      'tr': 'tr-TR',
      'uk': 'uk-UA',
      'zh': 'zh-CN'
    };

    return localeMap[this.currentLanguage] || 'en-US';
  }

  /**
   * Get human-readable language name
   */
  getLanguageName(code: LanguageCode): string {
    const names: Record<LanguageCode, string> = {
      'ar': 'العربية',
      'bg': 'Български',
      'bn': 'বাংলা',
      'cs': 'Čeština',
      'da': 'Dansk',
      'de': 'Deutsch',
      'el': 'Ελληνικά',
      'en': 'English',
      'es': 'Español',
      'fi': 'Suomi',
      'fr': 'Français',
      'hi': 'हिन्दी',
      'hu': 'Magyar',
      'id': 'Bahasa Indonesia',
      'it': 'Italiano',
      'ja': '日本語',
      'ko': '한국어',
      'no': 'Norsk',
      'pl': 'Polski',
      'pt': 'Português',
      'ro': 'Română',
      'ru': 'Русский',
      'sr': 'Српски',
      'sv': 'Svenska',
      'tr': 'Türkçe',
      'uk': 'Українська',
      'zh': '简体中文'
    };
    return names[code] || code;
  }

  /**
   * Get list of supported languages
   */
  getSupportedLanguages(): ReadonlyArray<LanguageCode> {
    return this.supportedLanguages;
  }

  /**
   * Dispatch custom event
   */
  private dispatchEvent(eventName: string, detail: I18nEventDetail): void {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  /**
   * Check if translations are loaded for a language
   */
  hasTranslations(lang: LanguageCode): boolean {
    return !!this.translations[lang];
  }
}

// Initialize and export i18n instance to window
const i18nInstance = new I18n();
window.i18n = i18nInstance;

// Export type for use in type declarations
export type I18nInstance = I18n;

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    i18nInstance.init();
  });
} else {
  // DOM already loaded
  i18nInstance.init();
}
